import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import {
  OpenInNew,
  PlayCircleOutline,
  CollectionsOutlined,
  Instagram as InstagramIcon,
} from '@mui/icons-material';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { getCardStyles, getSecondaryButtonStyles, textMeasure } from '../../shared/styles';

const contentUseCase = new ContentUseCase();

export interface InstagramPost {
  id: string;
  caption: string;
  mediaType: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  imageUrl: string | null;
  permalink: string;
  timestamp: string;
}

interface FeedResponse {
  configured: boolean;
  posts: InstagramPost[];
  next: string | null;
}

const ENDPOINT = '/.netlify/functions/instagram';

interface InstagramFeedProps {
  /** Posts to show. Omit for the full feed with a "load more" button. */
  limit?: number;
  /**
   * Whether the panel that stands in for the posts offers a link to the
   * profile. Off where the surrounding page already carries that link, so a
   * failed request does not put two identical buttons under each other.
   */
  profileLink?: boolean;
}

/**
 * Renders our Instagram posts. Captions are shown in full so the page is
 * readable without opening Instagram — that is the point of having it here.
 *
 * The feed degrades in three steps: posts if the API answers, an invitation to
 * the profile if no token is configured yet, and an explanatory notice if the
 * request fails. All three share one panel, so the space the posts would take
 * never collapses into a stray line of text.
 */
export const InstagramFeed: React.FC<InstagramFeedProps> = ({ limit, profileLink = true }) => {
  const theme = useTheme();
  const { language } = useLanguage();
  const social = contentUseCase.getSocialMediaContent(language);
  const copy = social.page;

  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'unconfigured'>('loading');
  const [loadingMore, setLoadingMore] = useState(false);

  const showLoadMore = limit === undefined && cursor !== null;

  const load = useCallback(
    async (after?: string) => {
      const params = new URLSearchParams();
      if (limit !== undefined) params.set('limit', String(limit));
      if (after) params.set('after', after);

      const response = await fetch(`${ENDPOINT}?${params.toString()}`);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return (await response.json()) as FeedResponse;
    },
    [limit]
  );

  useEffect(() => {
    let cancelled = false;

    load()
      .then((data) => {
        if (cancelled) return;
        if (!data.configured) {
          setStatus('unconfigured');
          return;
        }
        setPosts(data.posts);
        setCursor(data.next);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [load]);

  const handleLoadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const data = await load(cursor);
      setPosts((current) => [...current, ...data.posts]);
      setCursor(data.next);
    } catch {
      setStatus('error');
    } finally {
      setLoadingMore(false);
    }
  };

  /**
   * What stands where the posts would be: while they load, when there are
   * none, and when the request fails. One panel for all three, because a bare
   * line of text and a button under it left the page looking broken rather
   * than simply quiet - and `status` announces the change to a screen reader
   * without moving focus.
   */
  const placeholder = (message: string, busy = false) => (
    <Paper
      elevation={0}
      role="status"
      sx={{
        // Outlined rather than tinted: this panel appears on a white section
        // (the start page) and on a grey one (the Instagram page), and a card
        // surface with a border reads on either, where a tint only reads on
        // white. The hover state goes, because nothing here is clickable.
        ...getCardStyles(theme, 'outlined'),
        '&:hover': {},
        textAlign: 'center',
        p: { xs: 4, md: 6 },
      }}
    >
      {busy ? (
        <CircularProgress size={28} sx={{ mb: 2, color: 'primary.dark' }} />
      ) : (
        <InstagramIcon sx={{ fontSize: 36, color: 'primary.dark', mb: 2 }} />
      )}
      <Typography
        variant="body1"
        sx={{ color: 'text.secondary', ...textMeasure, mx: 'auto', mb: profileLink ? 3.5 : 0 }}
      >
        {message}
      </Typography>
      {profileLink && (
        <Button
          href={social.instagram.url}
          target="_blank"
          rel="noopener noreferrer me"
          sx={getSecondaryButtonStyles(theme)}
          startIcon={<InstagramIcon />}
        >
          {copy.followButton}
        </Button>
      )}
    </Paper>
  );

  if (status === 'loading') return placeholder(copy.loading, true);
  if (status === 'unconfigured') return placeholder(social.instagram.description);
  if (status === 'error') return placeholder(copy.error);
  if (posts.length === 0) return placeholder(copy.empty);

  const dateFormatter = new Intl.DateTimeFormat(language === 'de' ? 'de-DE' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <Box>
      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(300px, 1fr))' },
          gap: 3,
        }}
      >
        {posts.map((post) => {
          const date = dateFormatter.format(new Date(post.timestamp));
          const badge =
            post.mediaType === 'VIDEO'
              ? { icon: <PlayCircleOutline fontSize="small" />, label: copy.videoLabel }
              : post.mediaType === 'CAROUSEL_ALBUM'
                ? { icon: <CollectionsOutlined fontSize="small" />, label: copy.albumLabel }
                : null;

          return (
            <Paper
              key={post.id}
              component="li"
              elevation={0}
              sx={{ ...getCardStyles(theme, 'outlined'), p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
              {post.imageUrl && (
                <Box sx={{ position: 'relative', backgroundColor: theme.palette.grey[100] }}>
                  <Box
                    component="img"
                    src={post.imageUrl}
                    // The caption carries the meaning and is rendered right
                    // below, so repeating it here would duplicate it for screen
                    // reader users.
                    alt=""
                    loading="lazy"
                    sx={{ display: 'block', width: '100%', aspectRatio: '1 / 1', objectFit: 'cover' }}
                  />
                  {badge && (
                    <Chip
                      icon={badge.icon}
                      label={badge.label}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        backgroundColor: 'rgba(0,0,0,0.65)',
                        color: '#FFFFFF',
                        '& .MuiChip-icon': { color: '#FFFFFF' },
                      }}
                    />
                  )}
                </Box>
              )}
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" component="time" dateTime={post.timestamp}>
                  {date}
                </Typography>
                {post.caption && (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'text.primary' }}>
                    {post.caption}
                  </Typography>
                )}
                <MuiLink
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    mt: 'auto',
                    pt: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'primary.dark',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {copy.openPost}
                  <OpenInNew sx={{ fontSize: 16 }} />
                </MuiLink>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {showLoadMore && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button onClick={handleLoadMore} disabled={loadingMore} sx={getSecondaryButtonStyles(theme)}>
            {loadingMore ? copy.loading : copy.loadMore}
          </Button>
        </Box>
      )}
    </Box>
  );
};
